// extension methods
import './shared/utils/array-extensions';
import './shared/utils/globals';
// angular
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { registerLocaleData, CommonModule } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeIt from '@angular/common/locales/it';
import localeItExtra from '@angular/common/locales/extra/it';
import localeEn from '@angular/common/locales/en-GB';
import localeEnExtra from '@angular/common/locales/extra/en-GB';
// 3rd party
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgbModule, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
// modules
import { RoomInfoScreenModule } from './modules/room-info-screen/room-info-screen.module';
import { RoomReservationModule } from './modules/room-reservation/room-reservation.module';
import { AppRoutingModule } from './modules/app-routing/app-routing.module';
// services
import { AppConfigService } from './shared/services/app-config.service';
import { UserSettingsService } from './shared/services/rest/user-settings.service';
import { AuthorizationService } from './shared/services/authorization.service';
import { LanguageService } from './shared/services/language.service';
import { MessageService } from './shared/services/message.service';
import { StorageService } from './shared/services/storage.service';
import { CacheService } from './shared/services/cache.service';
// components
import { LanguageSelectorComponent } from './shared/components/full-app-component/components/language-selector/language-selector.component';
import { FullAppComponentComponent } from './shared/components/full-app-component/full-app-component.component';
import { SingleAppComponentComponent } from './shared/components/single-app-component/single-app-component.component';
import { ApplicationMenuComponent } from './shared/components/full-app-component/components/application-menu/application-menu.component';
import {
  ApplicationFooterComponent
} from './shared/components/full-app-component/components/application-footer/application-footer.component';
import { AppComponent } from './app.component';
import { BuildingService } from './shared/services/rest/building.service';
import { RoomService } from './shared/services/rest/room.service';
import { RoomPublicService } from './shared/services/rest/room-public.service';
import { TokenService } from './shared/services/token.service';
import { PageNotFoundComponent } from './shared/components/page-not-found/page-not-found.component';
import { LogoutComponent } from './shared/components/logout/logout.component';
import { ClxDatepickerI18nService } from './shared/services/clx-datepicker-i18n.service';
import { LoginHandlerService } from './shared/services/login-handler.service';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, AppConfigService.translationsPath);
}

export function AppConfigFactory(http: HttpClient, appConfig: AppConfigService): () => Promise<void> {
  return () => appConfig.load()
    .then(() => {
      const params = GetUrlTokenParams();
      if (params.access_token !== null || params.code !== null) {
        //self construct service because appConfig.load() is a prerequisite
        const loginHandler = new LoginHandlerService(http, new StorageService());
        return loginHandler.handleTokenResponse(params);
      }
      return Promise.resolve();
    });
}

/**
 * Retrurns token related url params
 *
 * @export
 * @return {{ access_token: string, code: string }}
 */
export function GetUrlTokenParams(): { access_token: string, code: string } {
  const params: { access_token: string, code: string } = { access_token: null, code: null };
  const args = window.location.search;
  if (args) {
    args.replace('?', '').split('&').forEach(arg => {
      var parts = arg.split('=');
      params[parts[0]] = parts[1];
    });
  }
  return params;
}

registerLocaleData(localeDe, 'de-CH', localeDeExtra);
registerLocaleData(localeFr, 'fr-CH', localeFrExtra);
registerLocaleData(localeIt, 'it-CH', localeItExtra);
registerLocaleData(localeEn, 'en-GB', localeEnExtra);

/**
 * Main module to kick-start the app with a minimum set of dependencies
 *
 * @export
 * @class AppModule
 */
@NgModule({
  declarations: [
    // MessageComponent,
    // MessagesComponent,
    LanguageSelectorComponent,
    SingleAppComponentComponent,
    FullAppComponentComponent,
    ApplicationMenuComponent,
    ApplicationFooterComponent,
    AppComponent,
    PageNotFoundComponent,
    LogoutComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    HttpClientModule,
    NgbModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    RoomReservationModule,
    RoomInfoScreenModule,
    // must be last
    AppRoutingModule
  ],
  providers: [
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: AppConfigFactory,
      deps: [
        HttpClient,
        AppConfigService
      ],
      multi: true
    },
    {
      provide: NgbDatepickerI18n,
      useClass: ClxDatepickerI18nService
    },
    AuthorizationService,
    LanguageService,
    MessageService,
    StorageService,
    UserSettingsService,
    BuildingService,
    RoomService,
    RoomPublicService,
    CacheService,
    TokenService
  ],
  bootstrap: [
    AppComponent
  ]
})

export class AppModule { }
